import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import IntegrationQueueTable from "../../components/IntegrationQueueTable";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ENABLED_OPTIONS = [
  { value: "enabled", label: "Ativado" },
  { value: "disabled", label: "Desativado" },
];

const GlpiIntegration = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [url, setUrl] = useState("");
  const [appToken, setAppToken] = useState("");
  const [userToken, setUserToken] = useState("");
  const [entityId, setEntityId] = useState("0");
  const [categoryId, setCategoryId] = useState("1");
  const [autoCreate, setAutoCreate] = useState("enabled");
  const [syncClose, setSyncClose] = useState("enabled");
  const [queues, setQueues] = useState([]);
  const [enabledQueueIds, setEnabledQueueIds] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/integrations/glpi");
        setUrl(data.url || "");
        setAppToken(data.appToken || "");
        setUserToken(data.userToken || "");
        setEntityId(data.entityId || "0");
        setCategoryId(data.categoryId || "1");
        setAutoCreate(data.autoCreate || "enabled");
        setSyncClose(data.syncClose || "enabled");
        setQueues(data.queues || []);
        setEnabledQueueIds(data.enabledQueueIds || []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggleQueue = (queueId, checked) => {
    setEnabledQueueIds(prev => {
      const id = Number(queueId);
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter(item => item !== id);
    });
  };

  const handleSave = async () => {
    if (!url.trim()) {
      toast.error("Informe a URL do GLPI.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/integrations/glpi", {
        url,
        appToken,
        userToken,
        entityId,
        categoryId,
        autoCreate,
        syncClose,
        enabledQueueIds,
      });
      setEnabledQueueIds(data.enabledQueueIds || []);
      toast.success("Integração GLPI salva com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress color="primary" />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>GLPI</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" component={Link} to="/integracoes">
            Voltar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            onClick={handleSave}
          >
            Salvar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, fontWeight: 600 }}>
              <SupportAgentIcon color="primary" />
              Conexão GLPI
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="URL do GLPI"
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://glpi.suaempresa.com.br"
              />
              <TextField
                fullWidth
                type="password"
                label="App Token"
                value={appToken}
                onChange={e => setAppToken(e.target.value)}
              />
              <TextField
                fullWidth
                type="password"
                label="User Token"
                value={userToken}
                onChange={e => setUserToken(e.target.value)}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Entity ID" value={entityId} onChange={e => setEntityId(e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Category ID" value={categoryId} onChange={e => setCategoryId(e.target.value)} />
                </Grid>
              </Grid>

              <TextField select fullWidth label="Abrir chamado ao atribuir fila" value={autoCreate} onChange={e => setAutoCreate(e.target.value)}>
                {ENABLED_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
              <TextField select fullWidth label="Encerrar chamado ao finalizar ticket" value={syncClose} onChange={e => setSyncClose(e.target.value)}>
                {ENABLED_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>

              <Typography variant="body2" color="textSecondary">
                O ID do chamado GLPI é armazenado no ticket local para manutenção e sincronização de status.
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7} sx={{ display: "flex", minHeight: 0 }}>
          <IntegrationQueueTable
            queues={queues}
            enabledQueueIds={enabledQueueIds}
            onToggle={handleToggleQueue}
            title="Ativação por fila"
            description="Escolha em quais filas os tickets serão sincronizados com o GLPI."
          />
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default GlpiIntegration;
